import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { useCommentMarkerDrag } from '@src/components/content-components/comments/useCommentMarkerDrag';
import CircleCheckIcon from '@src/svgs/CircleCheckIcon';
import { stripMentionTokens } from '@src/utility/commentMentions';
import { formatRelativeTimeShort } from '@src/utility/relativeTime';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import { timeFormat } from '@src/utility/timeFormat';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

/** A pinned comment reduced to what a scrubber marker + hover card need. */
export type ClipCommentMarker = {
  id: string;
  /** Playhead position, seconds. */
  time: number;
  /** 1-based thread number shown as "#N" in the hover card. */
  index: number;
  authorName: string;
  /** Fallback for the author color when `authorColor` is absent. */
  authorId: string | null;
  /** Collision-free roster color (stamped by the modal); hash fallback. */
  authorColor?: string;
  body: string;
  /** ISO create time for the relative timestamp. */
  createdAt: string;
  /** Resolved threads render dimmed dots + a Resolved badge on the card. */
  isResolved: boolean;
  /**
   * Whether the current user may reposition this comment. Author-only, mirroring
   * the edit/delete gate — moving a comment changes what it points at.
   */
  canMove?: boolean;
};

type Props = {
  markers: ClipCommentMarker[];
  /** Clip length, seconds; positions each dot as `time / duration`. */
  duration: number;
  framerate: number;
  /**
   * Seek the preview to a marker's timestamp. The comment id comes with it so
   * the host can reveal that comment — the dot should behave like the timecode
   * chip in the list, which seeks AND shows the comment's drawing (PLAT-11291).
   * For a cluster it is the earliest member, the same comment the jump targets.
   */
  onJump: (time: number, commentId?: string) => void;
  /**
   * Persist a comment's new timestamp. **Omit to disable dragging entirely** —
   * this component is prop-only and reusable (the public share page is expected
   * to render markers read-only), so the affordance is opt-in rather than
   * something a new consumer inherits by accident.
   */
  onMove?: (id: string, time: number) => void;
  /**
   * Scrub the preview during a drag so the user sees the frame they're landing
   * on, without the play-resume that `onJump` performs.
   */
  onScrubPreview?: (time: number) => void;
};

/**
 * Distance (px) within which two markers are treated as the same spot. Equal to
 * the dot's hit target: any closer and their 20px buttons overlap, so only the
 * last one in DOM order is reachable and its status is the only one you see.
 */
const CLUSTER_DISTANCE_PX = 20;

/** Markers that share a spot on the rail, ordered by time. */
export type MarkerCluster = {
  /** Stable across renders: the earliest member's id. */
  key: string;
  /** Position + jump target — the earliest comment in the pile. */
  time: number;
  members: ClipCommentMarker[];
  /**
   * Dimmed only when EVERY member is resolved. A pile holding one open thread
   * must keep shouting, otherwise resolving one of two stacked comments looks
   * like resolving neither (PLAT-10951).
   */
  isResolved: boolean;
};

/**
 * Group markers whose dots would overlap on a track `trackWidth` px wide.
 * Greedy over time order: a marker joins the open cluster while it is within
 * one hit target of that cluster's first member, otherwise it starts a new one.
 */
export function clusterMarkers(
  markers: ClipCommentMarker[],
  duration: number,
  trackWidth: number,
): MarkerCluster[] {
  const sorted = [...markers].sort((a, b) => a.time - b.time);
  // Before the track has been measured, treat every marker as its own cluster
  // rather than collapsing them all into one.
  const gapSeconds =
    trackWidth > 0 && duration > 0
      ? (CLUSTER_DISTANCE_PX / trackWidth) * duration
      : 0;

  const clusters: MarkerCluster[] = [];
  for (const marker of sorted) {
    const open = clusters[clusters.length - 1];
    if (open && marker.time - open.time <= gapSeconds) {
      open.members.push(marker);
      open.isResolved = open.isResolved && marker.isResolved;
      continue;
    }
    clusters.push({
      key: marker.id,
      time: marker.time,
      members: [marker],
      isResolved: marker.isResolved,
    });
  }
  return clusters;
}

/**
 * Pinned-comment dots below the scrubber plus their hover cards. Rendered as a
 * direct child of the control bar's `Wrapper` (its `MarkerTrack` root full-bleeds
 * against the bar's padding) so a marker at time `t` sits under its position on
 * the rail above.
 *
 * A dot the current user owns can be dragged to reposition its comment; dots
 * that share a spot collapse into one counted dot (see `clusterMarkers`).
 */
export const ClipCommentMarkers = ({
  markers,
  duration,
  framerate,
  onJump,
  onMove,
  onScrubPreview,
}: Props) => {
  // Cluster whose hover card is showing, plus the dot's screen rect so the
  // portaled card can anchor above it (avoids control-bar clipping).
  const [hovered, setHovered] = useState<{
    cluster: MarkerCluster;
    rect: DOMRect;
  } | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  // Clustering is a pixel question, so the track's width is an input. Remeasure
  // on resize — the bar reflows on fullscreen toggles and window resizes.
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    setTrackWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width != null) setTrackWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { drag, startDrag } = useCommentMarkerDrag({
    trackRef,
    duration,
    framerate,
    onPreview: onScrubPreview,
    onCommit: (id, time) => onMove?.(id, time),
    onClick: (id, time) => onJump(time, id),
    // Put the preview back where the marker was, so an aborted drag leaves
    // nothing changed.
    onCancel: target => onScrubPreview?.(target.time),
  });

  const clusters = useMemo(
    // The dragged marker renders separately at its live position, so keep it
    // out of clustering — otherwise it would merge into a pile mid-gesture.
    () =>
      clusterMarkers(
        drag ? markers.filter(m => m.id !== drag.id) : markers,
        duration,
        trackWidth,
      ),
    [markers, duration, trackWidth, drag],
  );

  const dragged = drag ? (markers.find(m => m.id === drag.id) ?? null) : null;

  if (duration <= 0 || !markers.length) return null;

  const leftFor = (time: number) =>
    `${Math.min(100, (time / duration) * 100)}%`;

  return (
    <>
      {hovered &&
        !drag &&
        ReactDOM.createPortal(
          <CommentCard
            style={{
              left: hovered.rect.left + hovered.rect.width / 2,
              top: hovered.rect.top,
            }}
          >
            {hovered.cluster.members.map((marker, i) => (
              <CardEntry key={marker.id} $stacked={i > 0}>
                <CommentCardHeader>
                  <SpeakerAvatar
                    name={marker.authorName}
                    // Roster color stamped by the modal (collision-free, agreeing
                    // with the comment list + @-mention popover); hash fallback
                    // for authors outside the roster.
                    color={
                      marker.authorColor ??
                      getSpeakerColorFromLabel(
                        marker.authorName || marker.authorId,
                      )
                    }
                    size={20}
                    initialsColor="var(--gray-900)"
                  />
                  <CardAuthorBlock>
                    <CardAuthor>{marker.authorName}</CardAuthor>
                    <CardTimestamp>
                      {timeFormat(marker.time, framerate, true)}
                      {' · '}
                      {formatRelativeTimeShort(marker.createdAt)}
                    </CardTimestamp>
                  </CardAuthorBlock>
                  <CardMeta>
                    {marker.isResolved && (
                      <CardResolved>
                        <CircleCheckIcon
                          width="13"
                          height="13"
                          strokeColor="currentColor"
                        />
                        Resolved
                      </CardResolved>
                    )}
                    #{marker.index}
                  </CardMeta>
                </CommentCardHeader>
                <CommentCardBody $compact={hovered.cluster.members.length > 1}>
                  {stripMentionTokens(marker.body)}
                </CommentCardBody>
              </CardEntry>
            ))}
          </CommentCard>,
          document.body,
        )}
      <MarkerTrack ref={trackRef}>
        {clusters.map(cluster => {
          const [first] = cluster.members;
          const count = cluster.members.length;
          // A pile is never draggable: there is no honest answer to which dot
          // the cursor grabbed. Repositioning one of them goes through the
          // timecode field in the comment card.
          const draggable = count === 1 && !!onMove && !!first.canMove;
          return (
            <CommentMarker
              key={cluster.key}
              type="button"
              $resolved={cluster.isResolved}
              $draggable={draggable}
              $clustered={count > 1}
              aria-label={
                count > 1
                  ? `${count} comments at ${timeFormat(cluster.time, framerate, true)}`
                  : `Jump to comment by ${first.authorName} at ${timeFormat(
                      cluster.time,
                      framerate,
                      true,
                    )}`
              }
              style={{ left: leftFor(cluster.time) }}
              onMouseEnter={e =>
                setHovered({
                  cluster,
                  rect: e.currentTarget.getBoundingClientRect(),
                })
              }
              onMouseLeave={() => setHovered(null)}
              onMouseDown={
                draggable
                  ? e => startDrag(e, { id: first.id, time: first.time })
                  : undefined
              }
              // A draggable marker resolves its own MOUSE click through the drag
              // hook's sub-threshold path; wiring onClick too would fire the
              // jump twice, since the browser still emits a native click.
              onClick={
                draggable ? undefined : () => onJump(cluster.time, first.id)
              }
              // Keyboard activation never reaches the drag hook (Enter emits a
              // click with no preceding mousedown), so a draggable marker needs
              // its own key handler or your own comments are mouse-only.
              // `preventDefault` stops the browser synthesizing the click that
              // would double-jump the non-draggable case.
              //
              // Enter only, deliberately: the control bar claims Space
              // document-wide for play/pause, so activating on Space would both
              // jump and toggle playback.
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onJump(cluster.time, first.id);
                }
              }}
            >
              {count > 1 && <MarkerCount>{count}</MarkerCount>}
            </CommentMarker>
          );
        })}
        {drag && dragged && (
          <>
            <DragLabel style={{ left: leftFor(drag.time) }}>
              {timeFormat(drag.time, framerate, true)}
            </DragLabel>
            <CommentMarker
              as="div"
              aria-hidden="true"
              $resolved={dragged.isResolved}
              $dragging
              style={{ left: leftFor(drag.time) }}
            />
          </>
        )}
      </MarkerTrack>
    </>
  );
};

export default ClipCommentMarkers;

/**
 * Row of pinned-comment dots below the scrubber. Full-bleed to match the
 * scrubber's coordinate space so a marker at time `t` sits under its position
 * on the rail above.
 *
 * The height is load-bearing: `ControlBarSpacer` in `SocialClipShareModal`
 * hardcodes the control bar's total height and folds this row's 10px into it.
 * Drag chrome (the enlarged dot, the timecode label) therefore overflows the
 * row rather than growing it.
 */
const MarkerTrack = styled.div`
  position: relative;
  height: 10px;
  margin: 0 -12px;
`;

/**
 * A pinned-comment dot, positioned at its timestamp and centered. The button
 * is a 20px invisible hit target (an 8px dot is too small to hover/click
 * reliably); the visible dot is the ::after circle. Resolved threads dim so
 * open feedback stands out on the scrubber.
 */
const CommentMarker = styled.button<{
  $resolved?: boolean;
  $draggable?: boolean;
  $clustered?: boolean;
  $dragging?: boolean;
}>`
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  /* grab/grabbing signals "you can move this" before the user tries. */
  cursor: ${props =>
    props.$dragging ? 'grabbing' : props.$draggable ? 'grab' : 'pointer'};
  transform: translate(-50%, -50%);
  ${props => props.$dragging && 'pointer-events: none; z-index: 2;'}

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    /* A counted pile needs room for its number; a lone dot stays 6px. */
    width: ${props => (props.$clustered ? 14 : 6)}px;
    height: ${props => (props.$clustered ? 14 : 6)}px;
    border-radius: 50%;
    /* Status color links to the comment list: open = gold (needs attention),
       resolved = green (matching the list's resolve check) but at reduced
       opacity so "done" recedes rather than shouting. */
    background: ${props =>
      props.$resolved ? 'var(--primary)' : 'var(--accent)'};
    opacity: ${props => (props.$resolved ? 0.55 : 1)};
    transform: translate(-50%, -50%)
      ${props => (props.$dragging ? 'scale(1.4)' : '')};
    transition: transform 100ms ease-out;
  }

  &:hover {
    z-index: 1;

    &::after {
      transform: translate(-50%, -50%) scale(1.4);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -4px;
  }
`;

// The pile's size, drawn over the enlarged dot. Sits above the ::after circle
// so the number reads against the status color.
const MarkerCount = styled.span`
  position: relative;
  z-index: 1;
  color: var(--gray-900);
  font-size: 9px;
  font-weight: var(--font-weight-semibold, 600);
  line-height: 1;
  pointer-events: none;
`;

// Live timecode readout tracking a dragged dot. Overflows the row upward over
// the 4px rail — transient, and the alternative (growing the row) would desync
// the modal's hardcoded control-bar height.
const DragLabel = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  z-index: 3;
  padding: 2px 6px;
  border-radius: var(--radius-sm, 4px);
  background: var(--popover);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--elevation-md);
  color: var(--foreground);
  font-size: var(--text-xs, 12px);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
`;

/**
 * Hover preview of a comment, portaled to the body and anchored above its
 * dot. Non-interactive (the dot handles the jump click) so the mouse never
 * needs to reach it — which also lets a stacked card list several comments
 * without needing a hover bridge.
 */
const CommentCard = styled.div`
  position: fixed;
  transform: translate(-50%, calc(-100% - 8px));
  z-index: ${CLIP_PREVIEW_Z.quickEditPopover};
  width: 260px;
  max-width: 80vw;
  max-height: 320px;
  overflow-y: auto;
  padding: 12px 14px;
  background: var(--popover);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius, 8px);
  box-shadow: var(--elevation-md);
  pointer-events: none;
`;

// One comment inside the card. Stacked entries get a rule so a pile reads as
// separate comments rather than one run-on body.
const CardEntry = styled.div<{ $stacked?: boolean }>`
  ${props =>
    props.$stacked &&
    `
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid var(--border-subtle);
  `}
`;

// flex-start so the meta (Resolved / #N) tops out level with the author name
// rather than floating centered against the two-line name/time stack.
const CommentCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

// Name stacked over the relative time so long author names never crowd the
// timestamp into wrapping; the name itself truncates with an ellipsis.
const CardAuthorBlock = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
`;

// Compact header type: the name matches the list's author style (xs/medium)
// so the comment body — one step larger — reads as the card's main content.
const CardAuthor = styled.span`
  color: var(--foreground);
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-weight-medium, 500);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Video position + recency as one metadata line ("02:55 · 47 minutes ago");
// tabular numerals keep the timecode steady across markers.
const CardTimestamp = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs, 12px);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  margin-left: auto;
  color: var(--text-muted);
  font-size: var(--text-xs, 12px);
  /* Match the author name's line box so the header's flex-start alignment
     puts this row level with the name, not a few px below. */
  line-height: 1.2;
`;

// Green check + label marking a handled thread, matching the list's resolve
// indicator.
const CardResolved = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--primary);
`;

const CommentCardBody = styled.div<{ $compact?: boolean }>`
  /* Same gray as the comment list body (design-system secondary text). */
  color: var(--text-secondary);
  font-size: var(--text-sm, 14px);
  line-height: 1.4;
  word-break: break-word;
  /* Long comments preview clamped; the full text lives in the Comments tab.
     A stacked card clamps harder so a pile stays scannable. */
  display: -webkit-box;
  -webkit-line-clamp: ${props => (props.$compact ? 2 : 4)};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
